import { BlinkIdScanningResult } from '@microblink/blinkid-core';
import dayjs, { Dayjs } from 'dayjs';

import { ISelectModel } from 'common/models';
import { EVisitorStatus } from 'modules/_common/consts/eVisitorStatus';
import {
  ICalculationVisitDetails,
  IReservationTypeDetails,
} from 'modules/accommodations/features/reservations/models';
import { IReservationDetails } from 'modules/lite/reservations/models';

import { eVisitorDocumentTypes } from '../consts/eVisitorDocumentTypes';

interface IProps {
  guest?: ICalculationVisitDetails;
  reservation?: IReservationTypeDetails | IReservationDetails;
  arrivalTypes?: ISelectModel<string>[];
  cities?: ISelectModel<string>[];
  countries?: ISelectModel<string>[];
  documentTypes?: ISelectModel<string>[];
  residenceTaxes?: ISelectModel<string>[];
  services?: ISelectModel<string>[];
  scannedData?: BlinkIdScanningResult;
}

export const useGuestForm = ({
  guest,
  cities,
  countries,
  documentTypes,
  residenceTaxes,
  arrivalTypes,
  services,
  reservation,
}: IProps) => {
  const adjustDataForForm = () => {
    const initialValues = {
      firstName: '' as string | undefined,
      middleName: '' as string | undefined,
      lastName: '' as string | undefined,
      documentNumber: '' as string | undefined,

      birthDate: dayjs(),

      ...guest,
      gender:
        guest?.gender !== undefined && guest.gender !== null
          ? guest.gender === true
            ? 'Z'
            : 'M'
          : undefined,
      guestCheckIn: false,
      serviceId: services ? services[0]?.value : undefined,
      arrivalType:
        guest?.arrivalType === null || guest?.arrivalType === undefined ? 'A' : guest.arrivalType,

      checkInDate: dayjs(!!guest?.checkInDate ? guest.checkInDate : reservation?.dateFrom),
      checkInTime: dayjs(!!guest?.checkInTime ? guest.checkInTime : '14:00', 'HH:mm'),
      checkOutDate: dayjs(!!guest?.checkOutDate ? guest.checkOutDate : reservation?.dateTo),
      checkOutTime: dayjs(!!guest?.checkOutTime ? guest.checkOutTime : '10:00', 'HH:mm'),
    };

    return {
      initialValues,
    };
  };

  const processPayload = ({
    values,
    isEvisitorCompleted,
  }: {
    values: any;
    isEvisitorCompleted?: boolean;
  }) => {
    const selectedDocumentType =
      documentTypes?.find(x => x.value === values?.documentTypeId) || undefined;
    const selectedResidenceCountry =
      countries?.find(x => x.value === values?.countryOfResidenceISO3) || undefined;
    const selectedCountryOfBirth =
      countries?.find(x => x.value === values?.countryOfBirthISO3) || undefined;
    const selectedService = services?.find(x => x.value === values?.serviceId) || undefined;
    const selectedResidenceTaxes =
      residenceTaxes?.find(x => x.value === values?.residenceTaxCategoryId) || undefined;
    const selectedArrivalType =
      arrivalTypes?.find(x => x.value === values?.arrivalType) || undefined;

    return {
      ...values,
      gender: values.gender === 'Z',
      guestCheckIn: isEvisitorCompleted ? true : false,
      documentType: selectedDocumentType?.label,
      countryOfResidence: selectedResidenceCountry?.label,
      countryOfBirth: selectedCountryOfBirth?.label,
      residenceTaxCategory: selectedResidenceTaxes?.label,
      serviceName: selectedService?.label,
      arrivalTypeName: selectedArrivalType?.label,
    };
  };

  const scanResultToForm = ({
    currentValues,
    values,
    setValues,
  }: {
    currentValues: any;
    values: BlinkIdScanningResult;
    setValues: any;
  }) => {
    // Extract text from StringResult objects (which have latin, arabic, cyrillic properties)
    const getStringValue = (field: any): string => {
      if (!field) return '';
      if (typeof field === 'string') return field;

      return (
        field.latin?.value ||
        field.arabic?.value ||
        field.cyrillic?.value ||
        field.greek?.value ||
        ''
      );
    };

    // Find MRZ result in subResults (MRZ is more reliable than VIZ)
    const mrzResult = values.subResults?.find((sub: any) => sub.mrz)?.mrz;

    // Use MRZ data first, fallback to VIZ data
    const firstName = mrzResult?.secondaryId || getStringValue(values.firstName);
    const lastName = mrzResult?.primaryId || getStringValue(values.lastName);
    const documentNumber = mrzResult?.documentNumber || getStringValue(values.documentNumber);
    const sex = mrzResult?.gender || getStringValue(values.sex);
    const nationalityCode = mrzResult?.nationality || getStringValue(values.nationality);
    const dateOfBirth = mrzResult?.dateOfBirth || values.dateOfBirth;
    const documentClassInfo = values.documentClassInfo;

    // Get nationality from ISO code
    let selectedNationality;
    const isoCode = documentClassInfo?.isoAlpha3CountryCode || nationalityCode;

    // Njemačka ima iznimku od MRZ standarda: travel.stackexchange.com/q/190534
    if (isoCode === 'D' || isoCode === 'DEU') {
      selectedNationality = countries?.find(x => x.value === 'DEU') || undefined;
    } else {
      selectedNationality = countries?.find(x => x.value === isoCode) || undefined;
    }

    // Parse date of birth
    let birthDate = dayjs();
    if (dateOfBirth?.day && dateOfBirth?.month && dateOfBirth?.year) {
      birthDate = dayjs()
        .year(dateOfBirth.year)
        .month(dateOfBirth.month - 1)
        .date(dateOfBirth.day);
    }

    const documentType = documentClassInfo?.type;
    const countryCode = documentClassInfo?.isoAlpha3CountryCode;

    const scannedValues = {
      firstName: firstName,
      lastName: lastName,
      documentNumber: documentNumber,
      countryOfResidenceISO3: selectedNationality?.value,
      countryOfBirthISO3: selectedNationality?.value,
      citizenshipISO3: selectedNationality?.value,
      placeOfResidence: isoCode !== 'HRV' ? selectedNationality?.label : '',
      gender: String(sex).toUpperCase().includes('M')
        ? 'M'
        : String(sex).toUpperCase().includes('F')
        ? 'Z'
        : 'M',
      birthDate: birthDate,
      residenceTaxCategoryId: getTaxCategoryIdByAge(birthDate),
      documentTypeId: getDocumentTypeFromScan(documentType, countryCode),
    };

    setValues({ ...currentValues, ...scannedValues });
  };

  const getTaxCategoryIdByAge = (dob: Dayjs) => {
    const date = dayjs(dob);
    const age = Math.abs(date.diff(Date(), 'year'));

    if (age >= 12 && age <= 18) return '2';
    if (age >= 0 && age <= 12) return '1';
    if (age > 18) return '14';
  };

  const getDocumentTypeFromScan = (
    documentType: string | undefined,
    countryCode: string | undefined,
  ): eVisitorDocumentTypes | undefined => {
    const isDomestic = countryCode === 'HRV';
    const {
      OSOBNA_ISKAZNICA_DOMAĆA,
      OSOBNA_ISKAZNICA_STRANA,
      ZDRAVSTVENA_ISKAZNICA,
      DJEČJA_PUTOVNICA_DOMAĆA,
      DJEČJA_PUTOVNICA_STRANA,
      OSOBNA_PUTOVNICA_DOMAĆA,
      OSOBNA_PUTOVNICA_STRANA,
      VOZAČKA_DOZVOLA_DOMAĆA,
      VOZAČKA_DOZVOLA_STRANA,
    } = eVisitorDocumentTypes;

    switch (documentType?.toLowerCase()) {
      case 'id':
        return isDomestic ? OSOBNA_ISKAZNICA_DOMAĆA : OSOBNA_ISKAZNICA_STRANA;

      case 'dl':
        return isDomestic ? VOZAČKA_DOZVOLA_DOMAĆA : VOZAČKA_DOZVOLA_STRANA;

      case 'passport':
        return isDomestic ? OSOBNA_PUTOVNICA_DOMAĆA : OSOBNA_PUTOVNICA_STRANA;

      case 'passport-card':
        return isDomestic ? OSOBNA_PUTOVNICA_DOMAĆA : OSOBNA_PUTOVNICA_STRANA;

      case 'minors-passport':
        return isDomestic ? DJEČJA_PUTOVNICA_DOMAĆA : DJEČJA_PUTOVNICA_STRANA;

      case 'health-insurance-card':
        return ZDRAVSTVENA_ISKAZNICA;

      default:
        return undefined;
    }
  };

  return {
    adjustDataForForm,
    processPayload,
    scanResultToForm,
    getTaxCategoryIdByAge,
  };
};
